import { resource } from "@angular/core";
import { signalStore, withComputed, withMethods, withProps } from "@ngrx/signals";
import { CacheRepository } from "../decorators/cache.decorator";
import { SettingsService, SettingsObj } from "../services/settings.service";
import { S25Util } from "../util/s25-util";

export const ProductLicenseStore = signalStore(
    { providedIn: "root" },
    withProps(() => ({
        _resource: resource({
            loader: () => SettingsService.getSettings(),
        }),
    })),
    withComputed((store) => ({
        _unsafeValue: () => store._resource.hasValue() && store._resource.value(),
        value: () => (store._resource.hasValue() && store._resource.value()) || ({} as SettingsObj),
    })),
    withMethods((store) => ({
        async ensureLoaded(): Promise<void> {
            if (store._resource.hasValue()) {
                return;
            }
            store._resource.reload();
            await S25Util.waitFor(() => store._resource.hasValue(), 50);
        },
    })),
    withMethods((store) => ({
        refresh: () => {
            CacheRepository.invalidateFunction(SettingsService.getSettings);
            return store._resource.reload();
        },
        invalidate: () => {
            CacheRepository.invalidateFunction(SettingsService.getSettings);
            store._resource.set(undefined);
            return store._resource.reload();
        },
    })),
    withMethods((store) => ({
        hasEval25: (): boolean => {
            const settings = store.value();
            return S25Util.toBool(settings.CAN_USE_EVAL25);
        },
        hasOptimizer: (): boolean => {
            const settings = store.value();
            return S25Util.toBool(settings.CAN_USE_OPTIMIZER);
        },
        hasS25: (): boolean => {
            const settings = store.value();
            return S25Util.toBool(settings.CAN_USE_S25);
        },
        hasX25: (): boolean => {
            const settings = store.value();
            return S25Util.toBool(settings.CAN_USE_X25);
        },
        hasStudentEngagement: (): boolean => {
            const settings = store.value();
            return S25Util.toBool(settings.HAS_STUDENT_ENGAGEMENT);
        },
        hasLegacySis: (): boolean => {
            const settings = store.value();
            return S25Util.toBool(settings.HAS_LEGACY_SIS);
        },
        hasM25: (): boolean => {
            const settings = store.value();
            return S25Util.toBool(settings.M25_ENABLE);
        },
        hasSecurityGroups: (): boolean => {
            const settings = store.value();
            return S25Util.toBool(settings.CONFIG_SECURITY_GROUPS);
        },
        hasSyncBoundVcalEvents: (): boolean => {
            const settings = store.value();
            return S25Util.toBool(settings.SYNC_BOUND_VCAL_EVENTS);
        },
        hasNewFreshbooksApi: (): boolean => {
            const settings = store.value();
            return S25Util.toBool(settings.USE_NEW_API);
        },
    })),
);
export type ProductLicenseStore = InstanceType<typeof ProductLicenseStore>;
